import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import {
  ArmStateChip,
  Button,
  Card,
  Chip,
  ConfirmDeleteModal,
  EmptyState,
  GrantBarrierMark,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import {
  useAccessGroups,
  useAccessGroupMembers,
  useDoorCodes,
  useDoorPolicies,
  useDevices,
  useDoors,
  useLocation,
  useLocationCredentialUsage,
  useLocationTree,
  useSchedules,
  useZones,
} from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import type {
  AccessGroup,
  Door,
  DoorCode,
  DoorPolicy,
  LocationDetail,
  LocationNode,
  Zone,
} from '../api/types'
import { locationNames, locationPaths, mostCommonChildType } from '../lib/locations'
import { coveringZones as zonesCovering, ZONE_KIND_TONE, zoneLocationSummary } from '../lib/zones'
import { utcDate } from '../lib/format'
import { kindLabel, lastUsedAtWhere, lastUsedTitle } from './Credentials'
import { ZoneEditorModal } from '../components/features/ZoneEditorModal'
import { SensorStateChip } from '../components/features/SensorStateChip'

const LOCATION_TYPES = [
  'country',
  'state',
  'city',
  'address',
  'floor',
  'room',
  'apartment',
  'other',
] as const

/** Legend dots — token colors only. */
const TYPE_DOT: Record<string, string> = {
  country: 'bg-ostrel-navy',
  state: 'bg-ostrel-info',
  city: 'bg-ostrel-city',
  address: 'bg-ostrel-gold',
  floor: 'bg-ostrel-warn',
  room: 'bg-ostrel-ok',
  apartment: 'bg-ostrel-accent',
  other: 'bg-ostrel-dim',
}

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function countNodes(nodes: LocationNode[]): number {
  return nodes.reduce((n, node) => n + 1 + countNodes(node.children), 0)
}

function findNode(nodes: LocationNode[], uid: string): LocationNode | null {
  for (const node of nodes) {
    if (node.unique_id === uid) return node
    const hit = findNode(node.children, uid)
    if (hit) return hit
  }
  return null
}

/** Descendant locations in a subtree. */
function subtreeSize(node: LocationNode): number {
  let descendants = 0
  for (const child of node.children) {
    descendants += 1 + subtreeSize(child)
  }
  return descendants
}

/** Pre-order flatten for parent dropdowns. */
function flattenTree(nodes: LocationNode[], depth = 0): { uid: string; name: string; depth: number; barrier: boolean }[] {
  return nodes.flatMap((node) => [
    { uid: node.unique_id, name: node.name, depth, barrier: node.inherits_grants === false },
    ...flattenTree(node.children, depth + 1),
  ])
}

/** uid set of a node and everything beneath it (excluded from parent choices). */
function subtreeIds(node: LocationNode, into: Set<string> = new Set()): Set<string> {
  into.add(node.unique_id)
  for (const child of node.children) subtreeIds(child, into)
  return into
}

function TypeRadio({
  value,
  onChange,
}: {
  value: string
  onChange: (t: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Location type">
      {LOCATION_TYPES.map((t) => (
        <button
          key={t}
          type="button"
          role="radio"
          aria-checked={value === t}
          onClick={() => onChange(t)}
          className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
            value === t
              ? 'border-ostrel-gold bg-ostrel-gold/10 text-ostrel-gold'
              : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:bg-ostrel-sand'
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${TYPE_DOT[t] ?? 'bg-ostrel-dim'}`} />
          {t}
        </button>
      ))}
    </div>
  )
}

function ParentSelect({
  id,
  tree,
  value,
  exclude,
  onChange,
}: {
  id: string
  tree: LocationNode[]
  value: string
  exclude?: Set<string>
  onChange: (uid: string) => void
}) {
  const options = flattenTree(tree).filter((o) => !exclude?.has(o.uid))
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        Parent
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
      >
        <option value="">— none (root)</option>
        {options.map((o) => (
          <option key={o.uid} value={o.uid}>
            {'  '.repeat(o.depth)}
            {o.name}
            {o.barrier ? ' 🔒' : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

/** ACTIVE / EXPIRING / EXPIRED computed from valid_to. */
function doorCodeStatus(c: DoorCode): { tone: 'ok' | 'warn' | 'muted'; label: string } {
  if (c.valid_to) {
    const until = new Date(c.valid_to).getTime()
    if (!Number.isNaN(until)) {
      if (until < Date.now()) return { tone: 'muted', label: 'Expired' }
      if (until < Date.now() + 14 * 86400 * 1000) return { tone: 'warn', label: 'Expiring' }
    }
  }
  return { tone: 'ok', label: 'Active' }
}

/** Door-code validity is a calendar date stored at 00:00Z -- see `utcDate`. */
function fmtDate(iso: string | null): string | null {
  return iso ? utcDate(iso) : null
}

function GroupPolicyCard({
  group,
  policies,
  scheduleName,
}: {
  group: AccessGroup
  policies: DoorPolicy[]
  scheduleName: (uid: string | null) => string | null
}) {
  const members = useAccessGroupMembers(group.unique_id)
  return (
    <div className="rounded-sm border border-ostrel-line bg-ostrel-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ostrel-ink">
            <Link to={`/access-groups/${group.unique_id}`} className="hover:text-ostrel-accent">
              {group.name}
            </Link>
          </p>
          {group.description && (
            <p className="mt-0.5 truncate text-[11px] text-ostrel-ink-muted">{group.description}</p>
          )}
        </div>
        <span className="tnum shrink-0 text-[11px] text-ostrel-ink-muted">
          {members.data ? `${members.data.length} members` : '…'}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {policies.map((p) => (
          <span key={p.unique_id} className="flex items-center gap-1.5">
            <Chip tone={p.mode === 'exclude' ? 'danger' : 'ok'} label={p.mode} />
            <span className="tnum text-[11px] text-ostrel-ink-muted">
              {scheduleName(p.schedule) ?? 'Always'}
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}

function StatBox({
  label,
  value,
  hint,
  onClick,
}: {
  label: string
  value: number
  hint: string
  onClick?: () => void
}) {
  const clickable = Boolean(onClick)
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onClick}
      className={`flex flex-col gap-1 bg-ostrel-card p-3 text-left transition-colors ${
        clickable
          ? 'cursor-pointer hover:bg-ostrel-sand focus:outline-none focus:ring-2 focus:ring-ostrel-gold'
          : 'cursor-default'
      }`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        {label}
      </span>
      <span className="tnum text-2xl font-semibold text-ostrel-ink">{value}</span>
      <span className="text-[11px] text-ostrel-ink-muted">{hint}</span>
    </button>
  )
}

const USAGE_WINDOWS = [7, 30, 90] as const

/** "Credentials" — GET /locations/{uid}/credential-usage (issues #51/#54/
 *  #57): who occupies this location (and its subtree), every credential
 *  they hold — used or not — plus any other credential granted here in
 *  the window (a visitor, marked with a chip). Rows arrive sorted
 *  never-used first, then least recently used: the stale ones an operator
 *  should chase come first. "Last used" is the credential's GLOBAL last
 *  use; "Uses (N days)" counts `access.granted` events ANYWHERE in the
 *  window, not just at this location. */
export function CredentialUsageCard({ uid }: { uid: string }) {
  const [days, setDays] = useState<number>(30)
  const usage = useLocationCredentialUsage(uid, days)

  return (
    <Card
      title="Credentials"
      action={
        <div className="flex items-center gap-1.5">
          {USAGE_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              aria-pressed={days === w}
              onClick={() => setDays(w)}
              className={`tnum rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-1 focus:ring-ostrel-gold ${
                days === w
                  ? 'border-ostrel-gold/50 bg-ostrel-gold/10 text-ostrel-gold'
                  : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:text-ostrel-ink'
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      }
    >
      {usage.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      ) : usage.isError ? (
        <EmptyState title="Could not load credentials" hint={errText(usage.error)} />
      ) : (
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">Occupants</p>
            {usage.data.occupants.length === 0 ? (
              <p className="mt-1 text-xs text-ostrel-ink-muted">No occupants here.</p>
            ) : (
              <ul aria-label="Occupants" className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                {usage.data.occupants.map((o) => (
                  <li key={o.person} className="text-sm">
                    <Link to={`/directory/${o.person}`} className="text-ostrel-ink hover:text-ostrel-accent">
                      {o.person_name || o.person}
                    </Link>
                    <span className="ml-1 text-[10px] uppercase tracking-wider text-ostrel-ink-muted">{o.kind}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {usage.data.credentials.length === 0 ? (
            <EmptyState
              title="No credentials"
              hint={`No occupant credentials, and no access.granted events here in the last ${days} days.`}
            />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>Credential</TH>
                  <TH>Person</TH>
                  <TH>Last used</TH>
                  <TH className="text-right">Uses ({days} days)</TH>
                </tr>
              </THead>
              <TBody>
                {usage.data.credentials.map((row) => (
                  <TR key={row.credential}>
                    <TD>
                      <Link
                        to={`/credentials/${row.credential}`}
                        className="tnum block font-mono text-sm text-ostrel-ink hover:text-ostrel-accent"
                      >
                        {row.credential_code}
                      </Link>
                      <span className="tnum flex items-center gap-1.5 text-[10px] text-ostrel-ink-muted">
                        {kindLabel(row.credential_kind)}
                        {!row.occupant && (
                          <Chip
                            tone="muted"
                            label="visitor"
                            title="Not held by an occupant — granted here in the window"
                          />
                        )}
                      </span>
                    </TD>
                    <TD>
                      {row.person ? (
                        <Link
                          to={`/directory/${row.person}`}
                          className="text-sm text-ostrel-ink hover:text-ostrel-accent"
                        >
                          {row.person_name || row.person}
                        </Link>
                      ) : (
                        <span className="text-xs text-ostrel-ink-muted">Unassigned</span>
                      )}
                    </TD>
                    <TD>
                      <span className="tnum text-xs text-ostrel-ink-muted" title={lastUsedTitle(row)}>
                        {lastUsedAtWhere(row)}
                      </span>
                    </TD>
                    <TD className="text-right">
                      <span className="tnum text-sm text-ostrel-ink">{row.uses}</span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      )}
    </Card>
  )
}

function EditPanel({
  uid,
  tree,
  zones,
  doors,
  doorCodes,
  groups,
  policies,
  scheduleName,
  onAddChild,
  onDeleted,
}: {
  uid: string
  tree: LocationNode[]
  zones: Zone[]
  doors: Door[]
  doorCodes: DoorCode[]
  groups: AccessGroup[]
  policies: DoorPolicy[]
  scheduleName: (uid: string | null) => string | null
  onAddChild: (parentUid: string) => void
  onDeleted: (parentUid: string | null) => void
}) {
  const navigate = useNavigate()
  const customer = useAuthStore((s) => s.customer)
  const scope = customer?.unique_id ?? 'none'
  const queryClient = useQueryClient()
  const detail = useLocation(uid)
  const devices = useDevices()
  const sensorsHere = (devices.data ?? []).filter((d) => d.kind === 'sensor' && d.location === uid)

  const [name, setName] = useState('')
  const [type, setType] = useState<string>('other')
  const [parent, setParent] = useState('')
  const [externalRef, setExternalRef] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [inheritsGrants, setInheritsGrants] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(false)
  // undefined = closed, null = new zone, Zone = editing that zone.
  const [zoneEditing, setZoneEditing] = useState<Zone | null | undefined>(undefined)

  useEffect(() => {
    if (detail.data) {
      setName(detail.data.name)
      setType(detail.data.type)
      setParent(detail.data.parent ?? '')
      setExternalRef(detail.data.external_ref ?? '')
      setIdentifier(detail.data.identifier ?? '')
      setInheritsGrants(detail.data.inherits_grants ?? true)
    }
  }, [detail.data])

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [scope, 'locations'] })
  const save = useMutation({
    mutationFn: () =>
      api.updateLocation(uid, {
        type,
        name: name.trim(),
        external_ref: externalRef.trim() || undefined,
        parent: parent || null,
        inherits_grants: inheritsGrants,
        identifier: identifier.trim(),
      }),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: () => api.deleteLocation(uid),
    onSuccess: () => {
      setConfirmDelete(false)
      invalidate()
      onDeleted(detail.data?.parent ?? null)
    },
  })

  const paths = useMemo(() => locationPaths(tree), [tree])
  const names = useMemo(() => locationNames(tree), [tree])
  const node = useMemo(() => findNode(tree, uid), [tree, uid])
  const excluded = useMemo(() => (node ? subtreeIds(node) : new Set<string>()), [node])
  const descendants = node ? subtreeSize(node) : 0
  const subtree = useMemo(() => (node ? subtreeIds(node) : new Set([uid])), [node, uid])

  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-64" />
        <Skeleton className="h-28" />
        <Skeleton className="h-40" />
      </div>
    )
  }
  if (detail.isError || !detail.data) {
    return <EmptyState title="Could not load location" hint={detail.error ? errText(detail.error) : undefined} />
  }

  const d: LocationDetail = detail.data
  // Zones on this location or on any ancestor (a zone on an address covers its floors).
  const coveringZones = zonesCovering(zones, tree, uid)
  // Codes and policies reach this location via a door hung on it.
  const doorLocation = new Map(doors.map((door) => [door.unique_id, door.location]))
  const codesHere = doorCodes.filter((c) =>
    c.doors.some((doorUid) => subtree.has(doorLocation.get(doorUid) ?? '')),
  )
  // Policies target doors now — a policy reaches this location via a door hung on it.
  const policiesHere = policies.filter((p) => doorLocation.get(p.door) === uid)
  const groupsHere = groups.filter((g) => policiesHere.some((p) => p.group === g.unique_id))
  const warnings = Array.isArray(d.type_warnings) ? d.type_warnings : []

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <div className="flex flex-col gap-4">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Edit location · {paths.get(uid) ?? d.name}
          </p>
          {warnings.length > 0 && (
            <div className="rounded-sm border border-ostrel-warn/50 bg-ostrel-warn/10 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-warn">
                Type warnings
              </p>
              <ul className="mt-1.5 flex flex-col gap-1">
                {warnings.map((w) => (
                  <li key={w} className="text-xs text-ostrel-ink">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="flex flex-col gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Type
            </span>
            <TypeRadio value={type} onChange={setType} />
          </div>
          <ParentSelect id="edit-parent" tree={tree} value={parent} exclude={excluded} onChange={setParent} />
          <Input
            label="External reference"
            value={externalRef}
            onChange={(e) => setExternalRef(e.target.value)}
            placeholder="Optional vendor-side id"
          />
          <div className="flex flex-col gap-1">
            <Input
              label="Identifier (building codes)"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="e.g. 1 710"
            />
            <p className="text-[11px] text-ostrel-ink-muted">
              Space-separated prefixes matched from vendor data, e.g. apartment-number prefixes or the
              port number.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-ostrel-ink">
            <input
              type="checkbox"
              checked={!inheritsGrants}
              onChange={(e) => setInheritsGrants(!e.target.checked)}
              className="accent-ostrel-gold"
            />
            Require direct assignment (no inherited access)
          </label>
          <div id="location-zones" className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                Zones
              </span>
              <Button variant="ghost" size="sm" onClick={() => setZoneEditing(null)}>
                + Zone
              </Button>
            </div>
            {coveringZones.length === 0 ? (
              <p className="text-[11px] text-ostrel-ink-muted">No zones cover this location.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {coveringZones.map(({ zone: z, via }) => (
                  <li key={z.unique_id}>
                    <button
                      type="button"
                      onClick={() => setZoneEditing(z)}
                      title="Edit zone"
                      className="flex w-full flex-wrap items-center gap-1.5 rounded-sm px-1.5 py-1 text-left hover:bg-ostrel-sand focus:outline-none focus:ring-2 focus:ring-ostrel-gold"
                    >
                      <Chip tone={ZONE_KIND_TONE[z.kind] ?? 'muted'} label={z.kind.replace(/_/g, ' ')} />
                      <span className="text-sm text-ostrel-ink">{z.name}</span>
                      <span className="min-w-0 truncate text-[11px] text-ostrel-ink-muted">
                        {via ? `via ${names.get(via) ?? via}` : zoneLocationSummary(z, names)}
                      </span>
                      <span className="ml-auto">
                        <ArmStateChip state={z.arm_state} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {save.isError && <p className="text-xs text-ostrel-danger">{errText(save.error)}</p>}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="navy"
              disabled={save.isPending || !name.trim()}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            <Button variant="ghost" onClick={() => onAddChild(uid)}>
              Add child
            </Button>
            <Button variant="danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Assigned to this location">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-ostrel-line bg-ostrel-line sm:grid-cols-5">
          <StatBox
            label="Occupants"
            value={d.occupant_count ?? d.occupancies.length}
            hint="here and below"
            onClick={() => navigate(`/directory?location=${uid}`)}
          />
          <StatBox
            label="Child locations"
            value={descendants}
            hint="below this node"
            onClick={() => navigate(`/locations?parent=${uid}`)}
          />
          <StatBox
            label="Doors"
            value={doors.filter((door) => subtree.has(door.location)).length}
            hint="here and below"
            onClick={() => navigate(`/doors?location=${uid}`)}
          />
          <StatBox
            label="Door codes"
            value={codesHere.length}
            hint="on these doors"
            onClick={() => navigate(`/door-codes?location=${uid}`)}
          />
          <StatBox
            label="Zones"
            value={coveringZones.length}
            hint="here or above"
            onClick={() => document.getElementById('location-zones')?.scrollIntoView({ behavior: 'smooth' })}
          />
        </div>
        <p className="mt-3 text-[11px] text-ostrel-ink-muted">
          Occupants and doors include everything beneath this node; door codes are the codes on
          those doors (codes belong to doors, not places). Click a box to see the list. Devices
          themselves are managed under Systems.
        </p>
      </Card>

      <CredentialUsageCard uid={uid} />

      {sensorsHere.length > 0 && (
        <Card title="Sensors here">
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {sensorsHere.map((d) => (
              <li key={d.unique_id} className="flex items-center justify-between gap-3 py-2">
                <Link to={`/devices/${d.unique_id}`} className="min-w-0 hover:text-ostrel-gold">
                  <span className="block truncate text-sm text-ostrel-ink">{d.name}</span>
                  {d.vendor_ref && <span className="font-mono text-[10px] text-ostrel-ink-muted">{d.vendor_ref}</span>}
                </Link>
                <SensorStateChip state={d.sensor_state} changedAt={d.sensor_state_changed_at} />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Door codes on these doors">
        {codesHere.length === 0 ? (
          <EmptyState title="No door codes here" hint="Door codes appear once issued for this location." />
        ) : (
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {codesHere.map((c) => {
              const status = doorCodeStatus(c)
              const from = fmtDate(c.valid_from)
              const to = fmtDate(c.valid_to)
              return (
                <li key={c.unique_id} className="flex items-center gap-4 py-2.5">
                  <span className="tnum font-mono text-sm tracking-[0.35em] text-ostrel-ink">
                    {c.code}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[11px] text-ostrel-ink-muted">
                      {c.label || 'Door code'}
                    </span>
                    <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                      {scheduleName(c.schedule) ?? 'Always'}
                      {from || to ? ` · ${from ?? '…'} → ${to ?? '…'}` : ''}
                    </span>
                  </span>
                  <Chip tone={status.tone} label={status.label} />
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      <Card title="Access groups & door policies">
        {groupsHere.length === 0 ? (
          <EmptyState
            title="No groups reach this location"
            hint="Groups appear when a door policy includes this location."
          />
        ) : (
          <div className="flex flex-col gap-3">
            {groupsHere.map((g) => (
              <GroupPolicyCard
                key={g.unique_id}
                group={g}
                policies={policiesHere.filter((p) => p.group === g.unique_id)}
                scheduleName={scheduleName}
              />
            ))}
          </div>
        )}
      </Card>

      <ConfirmDeleteModal
        open={confirmDelete}
        title="Delete location"
        objectName={d.name}
        expected={uid}
        pending={remove.isPending}
        error={remove.isError ? errText(remove.error) : null}
        confirmLabel={remove.isPending ? 'Deleting…' : 'Delete'}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      >
        <p>
          Delete <span className="font-medium">{d.name}</span>?
          {descendants > 0 && (
            <>
              {' '}
              This also removes <span className="tnum font-medium">{descendants}</span>{' '}
              location{descendants === 1 ? '' : 's'} beneath it.
            </>
          )}
        </p>
      </ConfirmDeleteModal>

      <ZoneEditorModal
        open={zoneEditing !== undefined}
        zone={zoneEditing ?? null}
        initialLocations={[uid]}
        onClose={() => setZoneEditing(undefined)}
      />
    </div>
  )
}

function CreateLocationModal({
  open,
  parent,
  tree,
  onClose,
  onCreated,
}: {
  open: boolean
  parent: string | null
  tree: LocationNode[]
  onClose: () => void
  onCreated: (uid: string) => void
}) {
  const customer = useAuthStore((s) => s.customer)
  const scope = customer?.unique_id ?? 'none'
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [type, setType] = useState<string>('room')
  const [parentUid, setParentUid] = useState(parent ?? '')
  const [externalRef, setExternalRef] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [inheritsGrants, setInheritsGrants] = useState(true)

  const create = useMutation({
    mutationFn: () =>
      api.createLocation({
        type,
        name: name.trim(),
        external_ref: externalRef.trim() || undefined,
        parent: parentUid || null,
        inherits_grants: inheritsGrants,
        identifier: identifier.trim(),
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: [scope, 'locations'] })
      onClose()
      onCreated(created.unique_id)
    },
  })

  useEffect(() => {
    if (open) {
      setName('')
      // Default the type to whatever the parent mostly contains (an address
      // full of floors → "floor"); fall back to 'room' only when there's
      // nothing to infer from.
      setType((parentUid ? mostCommonChildType(tree, parentUid) : null) ?? 'room')
      setParentUid(parent ?? '')
      setExternalRef('')
      setIdentifier('')
      setInheritsGrants(true)
      create.reset()
    }
    // create.reset is stable; only reset when the modal (re)opens or the preset parent changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, parent])

  // Re-infer the default type when the operator picks a different parent.
  useEffect(() => {
    if (open && parentUid) {
      const inferred = mostCommonChildType(tree, parentUid)
      if (inferred) setType(inferred)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentUid])

  return (
    <Modal open={open} title="New location" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Type
          </span>
          <TypeRadio value={type} onChange={setType} />
        </div>
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Plan 2"
          autoFocus
        />
        <ParentSelect id="create-parent" tree={tree} value={parentUid} onChange={setParentUid} />
        <Input
          label="External reference"
          value={externalRef}
          onChange={(e) => setExternalRef(e.target.value)}
          placeholder="Optional vendor-side id"
        />
        <div className="flex flex-col gap-1">
          <Input
            label="Identifier (building codes)"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="e.g. 1 710"
          />
          <p className="text-[11px] text-ostrel-ink-muted">
            Space-separated prefixes matched from vendor data, e.g. apartment-number prefixes or the
            port number.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-ostrel-ink">
          <input
            type="checkbox"
            checked={!inheritsGrants}
            onChange={(e) => setInheritsGrants(!e.target.checked)}
            className="accent-ostrel-gold"
          />
          Require direct assignment (no inherited access)
        </label>
        {create.isError && <p className="text-xs text-ostrel-danger">{errText(create.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Creating…' : 'Create location'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function TreeRow({
  node,
  depth,
  collapsed,
  selectedUid,
  onToggle,
  onSelect,
}: {
  node: LocationNode
  depth: number
  collapsed: Set<string>
  selectedUid: string | null
  onToggle: (uid: string) => void
  onSelect: (uid: string) => void
}) {
  const hasChildren = node.children.length > 0
  const isCollapsed = collapsed.has(node.unique_id)
  const selected = node.unique_id === selectedUid
  return (
    <>
      <li>
        <div
          className={`flex w-full items-center gap-1.5 rounded-sm py-1.5 pr-2 text-left transition-colors ${
            selected
              ? 'bg-ostrel-sand shadow-[inset_2px_0_0_0_var(--color-ostrel-gold)]'
              : 'hover:bg-ostrel-sand/60'
          }`}
          style={{ paddingLeft: `${depth * 14 + 8}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              aria-expanded={!isCollapsed}
              onClick={() => onToggle(node.unique_id)}
              className="flex h-4 w-4 shrink-0 items-center justify-center text-ostrel-ink-muted hover:text-ostrel-ink focus:outline-none"
            >
              <span
                className={`inline-block text-[9px] transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
              >
                ▶
              </span>
            </button>
          ) : (
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              <span className={`h-1.5 w-1.5 rounded-full ${TYPE_DOT[node.type] ?? 'bg-ostrel-dim'}`} />
            </span>
          )}
          <button
            type="button"
            onClick={() => onSelect(node.unique_id)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none"
          >
            <span className="truncate text-sm font-medium text-ostrel-ink">{node.name}</span>
            {node.identifier && (
              <span className="tnum shrink-0 truncate text-[11px] text-ostrel-ink-muted">
                · {node.identifier}
              </span>
            )}
            {node.inherits_grants === false && <GrantBarrierMark />}
            <span className="ml-auto flex shrink-0 items-center gap-2">
              <span className="text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                {node.type}
              </span>
              <span className="tnum w-16 text-right text-[10px] text-ostrel-ink-muted">
                {hasChildren ? `${node.children.length}C` : ''}
              </span>
            </span>
          </button>
        </div>
      </li>
      {!isCollapsed &&
        node.children.map((child) => (
          <TreeRow
            key={child.unique_id}
            node={child}
            depth={depth + 1}
            collapsed={collapsed}
            selectedUid={selectedUid}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        ))}
    </>
  )
}

export default function LocationsPage() {
  const [params, setParams] = useSearchParams()
  const parentFilterUid = params.get('parent') ?? undefined

  const tree = useLocationTree()
  const zones = useZones()
  const doors = useDoors()
  const doorCodes = useDoorCodes()
  const groups = useAccessGroups()
  const policies = useDoorPolicies()
  const schedules = useSchedules()

  const [selectedUid, setSelectedUid] = useState<string | null>(parentFilterUid ?? null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)
  const [createParent, setCreateParent] = useState<string | null>(null)

  const visibleTree = useMemo(() => {
    if (!tree.data) return undefined
    if (!parentFilterUid) return tree.data
    const node = findNode(tree.data, parentFilterUid)
    return node ? [node] : tree.data
  }, [tree.data, parentFilterUid])

  const parentFilterNode = useMemo(
    () => (parentFilterUid && tree.data ? findNode(tree.data, parentFilterUid) : null),
    [parentFilterUid, tree.data],
  )

  // Select the first root once the tree arrives (and repair stale selection).
  useEffect(() => {
    if (!tree.data) return
    if (!selectedUid || !findNode(tree.data, selectedUid)) {
      setSelectedUid(visibleTree?.[0]?.unique_id ?? tree.data[0]?.unique_id ?? null)
    }
  }, [tree.data, selectedUid, visibleTree])

  const scheduleNames = useMemo(
    () => new Map((schedules.data ?? []).map((s) => [s.unique_id, s.name])),
    [schedules.data],
  )
  const scheduleName = (uid: string | null) => (uid ? (scheduleNames.get(uid) ?? uid) : null)

  const toggle = (uid: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })

  const openCreate = (parent: string | null) => {
    setCreateParent(parent)
    setCreateOpen(true)
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Estate"
        title="Locations"
        action={
          <>
            <Button variant="ghost" onClick={() => openCreate(null)}>
              + Root location
            </Button>
            <Button variant="primary" disabled={!selectedUid} onClick={() => openCreate(selectedUid)}>
              + Child of selected
            </Button>
          </>
        }
      />

      <div className="grid items-start gap-6 lg:grid-cols-5">
        <Card
          className="lg:col-span-2"
          title="Location tree"
          action={
            visibleTree && (
              <span className="tnum text-[11px] text-ostrel-ink-muted">
                {countNodes(visibleTree)} nodes
              </span>
            )
          }
        >
          {parentFilterNode && (
            <div className="mb-3 flex items-center gap-2 border-b border-ostrel-line pb-3 text-[11px] text-ostrel-ink-muted">
              <span>Showing descendants of</span>
              <Chip tone="info" label={parentFilterNode.name} />
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(params)
                  next.delete('parent')
                  setParams(next, { replace: true })
                  setSelectedUid(null)
                }}
                className="text-ostrel-gold hover:text-ostrel-accent"
              >
                Clear
              </button>
            </div>
          )}
          {tree.isPending ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-7" />
              <Skeleton className="h-7" />
              <Skeleton className="h-7" />
              <Skeleton className="h-7" />
              <Skeleton className="h-7" />
            </div>
          ) : tree.isError ? (
            <EmptyState title="Could not load locations" hint={errText(tree.error)} />
          ) : visibleTree?.length === 0 ? (
            <EmptyState
              title="No locations yet"
              hint="Create a root location to start modelling the estate."
              action={
                <Button variant="ghost" size="sm" onClick={() => openCreate(null)}>
                  + Root location
                </Button>
              }
            />
          ) : (
            <>
              <ul className="flex flex-col gap-0.5">
                {visibleTree?.map((node) => (
                  <TreeRow
                    key={node.unique_id}
                    node={node}
                    depth={0}
                    collapsed={collapsed}
                    selectedUid={selectedUid}
                    onToggle={toggle}
                    onSelect={setSelectedUid}
                  />
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5 border-t border-ostrel-line pt-3">
                {LOCATION_TYPES.map((t) => (
                  <span key={t} className="flex items-center gap-1">
                    <span className={`h-1.5 w-1.5 rounded-full ${TYPE_DOT[t]}`} />
                    <span className="text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                      {t}
                    </span>
                  </span>
                ))}
              </div>
            </>
          )}
        </Card>

        <div className="lg:col-span-3">
          {selectedUid && tree.data ? (
            <EditPanel
              key={selectedUid}
              uid={selectedUid}
              tree={tree.data}
              zones={zones.data ?? []}
              doors={doors.data ?? []}
              doorCodes={doorCodes.data ?? []}
              groups={groups.data ?? []}
              policies={policies.data ?? []}
              scheduleName={scheduleName}
              onAddChild={openCreate}
              onDeleted={(parentUid) => setSelectedUid(parentUid ?? tree.data[0]?.unique_id ?? null)}
            />
          ) : tree.data && tree.data.length > 0 ? (
            <Skeleton className="h-64" />
          ) : null}
        </div>
      </div>

      <CreateLocationModal
        open={createOpen}
        parent={createParent}
        tree={tree.data ?? []}
        onClose={() => setCreateOpen(false)}
        onCreated={setSelectedUid}
      />
    </div>
  )
}
